import { Box, Card, Chip, LinearProgress, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import Plotly from "plotly.js-basic-dist-min";
import type { JSX } from "react";
import { RefObject, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { SprengnetterLogo } from "@/assets";
import { formatFloatIndexTrendNoPercent } from "@/helpers/formatStringHelpers";
import { calcReferenceYearValues, getDateFormat } from "@/helpers/playgroundChartHelper";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { ChartSeries } from "@/types";
import { IndexType } from "@/types/IndexType";
import { ChartSeriesValue } from "@/types/playground/ChartSeriesValue";

import { setReferenceYear } from "../playgroundPageSlice";

enum QuickYears {
  threeY = "threeY",
  fiveY = "fiveY",
  tenY = "tenY",
  all = "all",
}

export type ChartAreaRef = {
  getChartAreaRef: () => RefObject<HTMLDivElement | null>;
};

type ChartAreaProps = {
  index: IndexType;
  chartSeries: ChartSeries[];
  isChartDataLoading: boolean;
  setHiddenChartSeries: React.Dispatch<React.SetStateAction<string[]>>;
};

interface PlotlyLegendClickEvent {
  curveNumber: number;
  data?: Plotly.Data[];
  layout?: Partial<Plotly.Layout>;
  frames?: Plotly.Frame[];
}

interface PlotlyHTMLElement extends HTMLDivElement {
  on(event: string, callback: (event: PlotlyLegendClickEvent) => void): void;
  removeAllListeners(event: string): void;
}

export const ChartArea = forwardRef<ChartAreaRef, ChartAreaProps>(
  ({ index, chartSeries, isChartDataLoading, setHiddenChartSeries }, ref): JSX.Element => {
    const dispatch = useAppDispatch();
    const plotRef = useRef<HTMLDivElement>(null);
    const [currentRange, setCurrentRange] = useState<QuickYears>(QuickYears.all);

    const { t } = useTranslation("PlaygroundPage");
    const { t: tCommon } = useTranslation();

    const referenceYear = useAppSelector((state) => state.playgroundPage.referenceYear);
    const timeResolution = useAppSelector((state) => state.playgroundPage.filter?.timeResolution);

    useImperativeHandle(ref, () => ({
      getChartAreaRef: (): RefObject<HTMLDivElement | null> => plotRef,
    }));

    const maxReferenceYear = chartSeries[0]?.values[chartSeries[0].values.length - 1]?.year;
    const minReferenceYear = chartSeries[0]?.values[0]?.year;

    useEffect(() => {
      if (referenceYear && minReferenceYear !== undefined && maxReferenceYear !== undefined) {
        const yearValue = referenceYear.year;

        if (yearValue < minReferenceYear) {
          const minDate = DateTime.fromObject({ year: minReferenceYear });
          dispatch(setReferenceYear(minDate));
        } else if (yearValue > maxReferenceYear) {
          const maxDate = DateTime.fromObject({ year: maxReferenceYear });
          dispatch(setReferenceYear(maxDate));
        }
      }
    }, [referenceYear, minReferenceYear, maxReferenceYear, dispatch]);

    const onChangeReferenceYear = (date: DateTime | null): void => {
      if (date && date.year <= maxReferenceYear && date.year >= minReferenceYear) dispatch(setReferenceYear(date));
    };

    const layout: Partial<Plotly.Layout> = useMemo(
      () => ({
        xaxis: {
          title: {
            text: tCommon(`indexes.${index}.x_axis_label`),
            font: { size: 16 },
          },
          showgrid: true,
          gridcolor: "#e5e5e5",
          type: "date",
        },
        yaxis: {
          title: {
            text: tCommon(`indexes.${index}.y_axis_label`),
            font: { size: 16 },
          },
          showgrid: true,
          gridcolor: "#e5e5e5",
        },
        showlegend: true,
        legend: {
          orientation: "h",
          yanchor: "top",
          xanchor: "center",
          y: -0.3,
          x: 0.5,
        },
        paper_bgcolor: "#ffffff",
        plot_bgcolor: "#ffffff",
      }),
      [index, tCommon],
    );

    useEffect(() => {
      const currentPlotRef = plotRef.current;
      if (!currentPlotRef) return;
      Plotly.newPlot(
        currentPlotRef,
        chartSeries.map((chartSeries) => {
          const valuesToUse = chartSeries.values.filter(
            (value): value is Required<ChartSeriesValue> => value.y != null,
          );

          return {
            x: valuesToUse.map((value) => value.x),
            y: valuesToUse.map((value) => value.y),
            hovertemplate: valuesToUse.map((value) => {
              const formattedY = formatFloatIndexTrendNoPercent(value.y);
              return `${chartSeries.name}<br >${index === IndexType.SIPN ? t("chart.pointDetails.pricePerSquareMeter") : t("chart.pointDetails.indexwert")} ${getDateFormat(index, timeResolution)}: ${formattedY}<br >${t("chart.pointDetails.entwicklungSeit")} ${referenceYear.year}: %{customdata}<extra></extra>`;
            }),
            type: "scatter",
            mode: "lines+markers",
            name: `${chartSeries.name}              `,
            line: { shape: "spline" },
            customdata: calcReferenceYearValues(referenceYear.year, chartSeries),
          };
        }),
        layout,
        {
          displaylogo: false,
          modeBarButtonsToRemove: ["toImage", "lasso2d", "select2d"],
          responsive: true,
          autosizable: true,
          displayModeBar: true,
        },
      );

      let clickTimeout: ReturnType<typeof setTimeout> | null = null;
      let lastClickTime = 0;

      (currentPlotRef as unknown as PlotlyHTMLElement).on(
        "plotly_legendclick",
        (event: PlotlyLegendClickEvent): void => {
          const clickedChartSeriesId = chartSeries[event.curveNumber].id;
          const currentTime = Date.now();

          if (clickTimeout) {
            clearTimeout(clickTimeout);
          }

          const isDoubleClick = currentTime - lastClickTime < 300;

          if (isDoubleClick) {
            setHiddenChartSeries((prev: string[]): string[] => {
              if (prev.length > 0 && prev.includes(clickedChartSeriesId)) return [];
              if (chartSeries.length - prev.length === 1 && !prev.includes(clickedChartSeriesId)) return [];
              if (prev.length === 0 || (prev.length > 0 && !prev.includes(clickedChartSeriesId)))
                return chartSeries.filter((series) => series.id !== clickedChartSeriesId).map((series) => series.id);
              return [];
            });

            lastClickTime = 0;
          } else {
            clickTimeout = setTimeout(() => {
              setHiddenChartSeries((prev: string[]): string[] => {
                const isCurrentlyHidden = prev.includes(clickedChartSeriesId);
                return isCurrentlyHidden
                  ? prev.filter((id: string) => id !== clickedChartSeriesId)
                  : [...prev, clickedChartSeriesId];
              });
            }, 300);

            lastClickTime = currentTime;
          }
        },
      );

      return (): void => {
        try {
          (currentPlotRef as unknown as PlotlyHTMLElement).removeAllListeners("plotly_legendclick");
        } catch {
          // Fallback to type assertion if method doesn't exist
          (currentPlotRef as unknown as PlotlyHTMLElement).removeAllListeners("plotly_legendclick");
        }
      };
    }, [chartSeries, currentRange, layout, referenceYear.year, setHiddenChartSeries, t, index, timeResolution]);

    const handleZoomReset = (): void => {
      Plotly.relayout(plotRef.current!, { "xaxis.autorange": true, "yaxis.autorange": true });
    };

    const handleSetRange = (range: QuickYears) => (): void => {
      const lastYear = chartSeries
        .map((chartSerie) => chartSerie.values[chartSerie.values.length - 1].year)
        .reduce((acc, cur) => Math.max(acc, cur), 0);

      if (range !== QuickYears.all) {
        let startYear;
        switch (range) {
          case QuickYears.threeY:
            startYear = lastYear - 3;
            break;
          case QuickYears.fiveY:
            startYear = lastYear - 5;
            break;
          case QuickYears.tenY:
            startYear = lastYear - 10;
            break;
          default:
            return;
        }

        Plotly.relayout(plotRef.current!, { "xaxis.range": [startYear.toString(), lastYear.toString()] });
      } else {
        handleZoomReset();
      }
      setCurrentRange(range);
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", mt: "20px" }}>
        {isChartDataLoading && <LinearProgress />}
        <Card variant="pageSection" sx={{ position: "relative" }}>
          <Box
            sx={(theme) => ({
              display: "flex",
              position: "absolute",
              top: theme.spacing(2),
              left: theme.spacing(2),
              zIndex: 2,
            })}
          >
            {Object.entries(QuickYears).map(([, value]) => (
              <Chip
                key={value}
                label={t("chart.quickYears.options", { context: value })}
                onClick={handleSetRange(value)}
                sx={{ mr: 1 }}
                color={currentRange === value ? "primary" : "default"}
              />
            ))}
          </Box>
          <div ref={plotRef} style={{ width: "100%", height: "500px" }}></div>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <DatePicker
                label={t("chart.referenceYear.title")}
                views={["year"]}
                value={referenceYear}
                onChange={onChangeReferenceYear}
                maxDate={DateTime.fromObject({ year: maxReferenceYear })}
                minDate={minReferenceYear ? DateTime.fromObject({ year: minReferenceYear }) : undefined}
                disabled={isChartDataLoading}
              />
              <Typography variant="hint">{t("chart.referenceYear.hint")}</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "end" }}>
              <SprengnetterLogo sx={{ width: "159px", height: "54px" }} />
              <Typography variant="hint">{t("chart.sourceInformation") + tCommon(`indexes.${index}.title`)}</Typography>
            </Box>
          </Box>
        </Card>
      </Box>
    );
  },
);
